import React, { useEffect, useMemo, useState } from 'react';
import { Shield, ShieldAlert, ShieldCheck, Trash2, RefreshCw, Loader2, ImageIcon, Type, Filter } from 'lucide-react';
import { adminApi } from '../../api';
import { PageHeader } from './AdminUI';

const KIND_META = {
  text: { label: 'Testo', icon: Type, tone: 'bg-amber-100 text-amber-800 border-amber-200' },
  image: { label: 'Immagine', icon: ImageIcon, tone: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
};

const FIELD_LABELS = {
  title: 'Titolo',
  description: 'Descrizione',
  image: 'Immagine',
};

function resolveImg(url) {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return (process.env.REACT_APP_BACKEND_URL || '') + url;
}

function formatDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('it-IT'); } catch { return iso; }
}

export default function AdminModerationLogs() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('blocked'); // 'blocked' | 'all'
  const [kind, setKind] = useState('all');         // 'all' | 'text' | 'image'
  const [sponsorFilter, setSponsorFilter] = useState('');

  const load = () => {
    setLoading(true);
    adminApi.moderationLogs(filter === 'blocked')
      .then(setItems)
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, [filter]);

  const sponsors = useMemo(() => {
    const set = new Set(items.map(i => i.sponsor_id).filter(Boolean));
    return Array.from(set);
  }, [items]);

  const rows = items.filter(i => {
    if (kind !== 'all' && i.kind !== kind) return false;
    if (sponsorFilter && i.sponsor_id !== sponsorFilter) return false;
    return true;
  });

  const counts = useMemo(() => ({
    total: items.length,
    blocked: items.filter(i => i.blocked).length,
    approved: items.filter(i => !i.blocked).length,
    text: items.filter(i => i.kind === 'text').length,
    image: items.filter(i => i.kind === 'image').length,
  }), [items]);

  const del = async (row) => {
    if (!window.confirm('Eliminare questo log?')) return;
    await adminApi.deleteModerationLog(row.id);
    load();
  };

  return (
    <div data-testid="admin-moderation-logs">
      <PageHeader
        title="Moderazione contenuti"
        subtitle={`${counts.total} eventi · ${counts.blocked} bloccati · ${counts.approved} approvati · ${counts.text} testo · ${counts.image} immagini`}
      />

      <div className="mb-4 flex items-start gap-3 p-4 rounded-lg border border-slate-200 bg-slate-50 text-slate-700 text-sm">
        <Shield size={18} className="mt-0.5 flex-shrink-0 text-[color:var(--yup-primary)]" />
        <div>
          <p className="font-semibold text-slate-800">Storico moderazione flash offer</p>
          <p className="text-xs mt-1 text-slate-600">
            Ogni volta che uno sponsor crea o modifica un'offerta, testo e immagine passano dal filtro. Qui vedi i contenuti bloccati (parolacce, nudità) e quelli approvati con AI. Puoi eliminare i falsi positivi.
          </p>
        </div>
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2 mb-4 items-center">
        <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 bg-white text-xs" data-testid="mod-filter-status">
          <button
            data-testid="mod-filter-blocked"
            onClick={() => setFilter('blocked')}
            className={`px-3 py-1.5 font-semibold ${filter === 'blocked' ? 'bg-red-500 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
          >Solo bloccati</button>
          <button
            data-testid="mod-filter-all"
            onClick={() => setFilter('all')}
            className={`px-3 py-1.5 font-semibold ${filter === 'all' ? 'bg-[color:var(--yup-primary)] text-black' : 'text-gray-600 hover:bg-gray-50'}`}
          >Tutti</button>
        </div>

        <div className="inline-flex rounded-lg overflow-hidden border border-gray-200 bg-white text-xs">
          {['all', 'text', 'image'].map(k => (
            <button
              key={k}
              data-testid={`mod-kind-${k}`}
              onClick={() => setKind(k)}
              className={`px-3 py-1.5 font-semibold ${kind === k ? 'bg-slate-800 text-white' : 'text-gray-600 hover:bg-gray-50'}`}
            >{k === 'all' ? 'Tutto' : k === 'text' ? 'Testo' : 'Immagine'}</button>
          ))}
        </div>

        {sponsors.length > 0 && (
          <select
            data-testid="mod-sponsor-select"
            value={sponsorFilter}
            onChange={e => setSponsorFilter(e.target.value)}
            className="text-xs border border-gray-200 bg-white rounded-lg px-3 py-1.5 text-gray-700"
          >
            <option value="">Tutti gli sponsor</option>
            {sponsors.map(s => <option key={s} value={s}>{s.substring(0, 12)}…</option>)}
          </select>
        )}

        <button
          onClick={load}
          data-testid="mod-refresh-btn"
          className="ml-auto inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-lg bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
        >
          <RefreshCw size={14} /> Aggiorna
        </button>
      </div>

      {loading ? (
        <div className="py-16 flex items-center justify-center text-slate-500">
          <Loader2 size={20} className="animate-spin mr-2" /> Caricamento...
        </div>
      ) : rows.length === 0 ? (
        <div className="py-16 text-center rounded-lg border border-dashed border-gray-200 bg-white">
          <ShieldCheck size={32} className="mx-auto text-emerald-500 mb-2" />
          <p className="text-sm text-slate-700 font-medium">Nessun evento di moderazione</p>
          <p className="text-xs text-slate-500 mt-1">Nessuno sponsor ha ancora inviato contenuti da moderare.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map(row => {
            const meta = KIND_META[row.kind] || KIND_META.text;
            const Icon = meta.icon;
            return (
              <div
                key={row.id}
                data-testid={`mod-row-${row.id}`}
                className={`rounded-xl bg-white border p-4 flex flex-col md:flex-row gap-4 ${row.blocked ? 'border-red-200' : 'border-emerald-200'}`}
              >
                {/* Left: image preview or icon */}
                <div className="flex-shrink-0 w-full md:w-28 h-28 rounded-lg overflow-hidden bg-slate-100 border border-slate-200 flex items-center justify-center">
                  {row.kind === 'image' && row.content_preview ? (
                    <img src={resolveImg(row.content_preview)} alt="preview" className="w-full h-full object-cover" />
                  ) : (
                    <Icon size={36} className="text-slate-400" />
                  )}
                </div>

                {/* Right: content */}
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2 mb-2">
                    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border ${meta.tone}`}>
                      <Icon size={11} /> {meta.label}
                    </span>
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold border border-slate-200 bg-slate-50 text-slate-700">
                      {FIELD_LABELS[row.field] || row.field}
                    </span>
                    {row.blocked ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-red-100 text-red-800 border border-red-200">
                        <ShieldAlert size={11} /> BLOCCATO
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200">
                        <ShieldCheck size={11} /> Approvato
                      </span>
                    )}
                    <span className="text-[11px] text-slate-500 ml-auto">{formatDate(row.created_at)}</span>
                  </div>

                  {row.reason && (
                    <div className="mb-2 text-sm">
                      <span className="text-slate-500">Motivo: </span>
                      <span className={row.blocked ? 'text-red-700 font-medium' : 'text-slate-800'}>{row.reason}</span>
                    </div>
                  )}

                  {row.kind === 'text' && row.content_preview && (
                    <div className="text-sm text-slate-700 bg-slate-50 rounded-lg px-3 py-2 border border-slate-200 whitespace-pre-wrap break-words">
                      {row.content_preview}
                    </div>
                  )}

                  <div className="mt-2 flex items-center justify-between gap-3">
                    <div className="text-[11px] text-slate-500 truncate">
                      Sponsor: <code className="bg-slate-100 rounded px-1 py-0.5 text-slate-700">{row.sponsor_id || '—'}</code>
                      {row.offer_id && (
                        <> · Offerta: <code className="bg-slate-100 rounded px-1 py-0.5 text-slate-700">{row.offer_id.substring(0, 8)}</code></>
                      )}
                    </div>
                    <button
                      onClick={() => del(row)}
                      data-testid={`mod-delete-${row.id}`}
                      className="inline-flex items-center gap-1 text-xs px-2.5 py-1.5 rounded-lg bg-white border border-slate-200 text-red-600 hover:bg-red-50"
                      title="Elimina log"
                    >
                      <Trash2 size={13} /> Elimina
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
